import { Injectable } from '@angular/core';
import { AGGREGATOR_CONSTANTS, AGGREGATOR_OPTIONS_CONSTANTS } from '../../shared/constants/aggregate-constants';
import { NumberFormattingUtil } from '../../shared/utils';
import { Aggregators } from './enums';
import { AggregationColumn, AggregationResult } from './models';

@Injectable({
    providedIn: 'root',
})
export class GridAggregateService {
    constructor() { }

    getNumberColumnSum(columnId: string, dataSet: any[], column: AggregationColumn): number | null {
        if (!dataSet || dataSet.length === 0) {
            return null;
        }

        const isMeasureofTime = this.isMeasureOfTime(column);

        return dataSet
            .map(item => parseFloat(item[columnId]))
            .filter(value => !isNaN(value))
            .map(value => isMeasureofTime ? Math.ceil(value) : value)
            .reduce((sum, value) => sum + value, 0);
    }

    getNumberColumnAverage(columnId: string, dataSet: any[], column: AggregationColumn): number | null {
        if (!dataSet || dataSet.length === 0) {
            return null;
        }

        const isMeasureofTime = this.isMeasureOfTime(column);

        const values = dataSet
            .map(item => parseFloat(item[columnId]))
            .filter(value => !isNaN(value))
            .map(value => isMeasureofTime ? Math.ceil(value) : value);

        return values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
    }

    getColumnCount(columnId: string, dataSet: any[]): number | null {
        if (!dataSet || dataSet.length === 0) {
            return null;
        }

        return dataSet
            .filter(item => {
                const value = item[columnId];
                return value !== null && value !== undefined && value !== '';
            })
            .length;
    }

    getNumberColumnMin(columnId: string, dataSet: any[], column: AggregationColumn): number | null {
        if (!dataSet || dataSet.length === 0) {
            return null;
        }

        const isMeasureofTime = this.isMeasureOfTime(column);

        const values = dataSet
            .map(item => parseFloat(item[columnId]))
            .filter(value => !isNaN(value))
            .map(value => isMeasureofTime ? Math.ceil(value) : value);

        return values.length > 0 ? Math.min(...values) : null;
    }

    getNumberColumnMax(columnId: string, dataSet: any[], column: AggregationColumn): number | null {
        if (!dataSet || dataSet.length === 0) {
            return null;
        }

        const isMeasureofTime = this.isMeasureOfTime(column);

        const values = dataSet
            .map(item => parseFloat(item[columnId]))
            .filter(value => !isNaN(value))
            .map(value => isMeasureofTime ? Math.ceil(value) : value);

        return values.length > 0 ? Math.max(...values) : null;
    }

    calculateAggregateValue(aggregatorType: string, columnId: string, dataset: any[], column: AggregationColumn, shouldDedupBySiteId: boolean): number | null {
        let filteredDataset;

        if (!column?.dataId && shouldDedupBySiteId) {
            // dedupe for repeated values for non-grid fields
            filteredDataset = this.deDuplicateDatasetBySiteId(dataset);
        }
        else {
            filteredDataset = dataset;
        }

        switch (aggregatorType) {
            case Aggregators.Sum:
                return this.getNumberColumnSum(columnId, filteredDataset, column);
            case Aggregators.Average:
                return this.getNumberColumnAverage(columnId, filteredDataset, column);
            case Aggregators.Min:
                return this.getNumberColumnMin(columnId, filteredDataset, column);
            case Aggregators.Max:
                return this.getNumberColumnMax(columnId, filteredDataset, column);
            case Aggregators.Count:
                return this.getColumnCount(columnId, filteredDataset);
            default:
                return null;
        }
    }

    getAggregationResult(
        aggregatorType: string, columnId: string, dataset: any[], column: AggregationColumn, parentItem: any, formatValueFn: (value: string, column: AggregationColumn) => string): AggregationResult {
        if (!aggregatorType || !dataset || dataset.length === 0) {
            return { label: '', value: '0', toolTip: '' };
        }

        const aggregatorOption = AGGREGATOR_OPTIONS_CONSTANTS.find(option => option.value === aggregatorType);
        const totalPrefix = !parentItem && (aggregatorType === Aggregators.Count || aggregatorType === Aggregators.Sum) ? 'Total ' : '';
        const label = aggregatorOption ? `${totalPrefix}${aggregatorOption.text}` : '';
        const toolTip = parentItem ? AGGREGATOR_CONSTANTS.SUBROW_AGGREGATE_TOOLTIP : AGGREGATOR_CONSTANTS.GRID_LEVEL_AGGREGATE_TOOLTIP;

        const rawValue = this.calculateAggregateValue(aggregatorType, columnId, dataset, column, false);
        const value = rawValue !== null
            ? (aggregatorType === Aggregators.Count ? rawValue.toString() : formatValueFn(rawValue.toString(), column))
            : '—';

        return { label, value, toolTip };
    }

    private isMeasureOfTime(column: AggregationColumn): boolean {
        // Support both Report Field format (FieldType.options.format) and Grid Column format (format)
        const format = column?.FieldType?.options?.format ?? column?.format;
        return format ? NumberFormattingUtil.isMeasureofTime(format) : false;
    }

    private deDuplicateDatasetBySiteId(dataset: any[]): any[] {
        return dataset.filter((item, index, source) => source.findIndex(candidate => candidate.SiteId === item.SiteId) === index);
    }
}
